// Live Class Game Mode — Lectura Live (the Kahoot-killer)
// Three visual modes in one page:
//   • lobby: pre-join screen (student joining a friend's game)
//   • host: projector view (big room code, timer, leaderboard, podium)
//   • player: student's mobile view (4 huge answer buttons)
// State polls every 1s. Power-ups (combo boost, shield, steal) are computed server-side.
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Zap, Shield, Sword, Users, Play, ChevronRight, Copy, Check,
  X, Sparkles, Crown, Medal, Flame, Loader2, Home, Skull,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import {
  startGame, joinGame, getGame, advanceGame, answerGame, endGame, reactInGame,
} from "@/lib/api";
import { toast } from "sonner";
import JeopardyGame from "@/pages/JeopardyGame";

// -------- tiny wav-less sound engine (Web Audio API tones) -----------
const useSfx = () => {
  const ctxRef = useRef(null);
  const ensure = () => {
    if (!ctxRef.current) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (Ctor) ctxRef.current = new Ctor();
    }
    return ctxRef.current;
  };
  const beep = (freq, dur = 0.12, type = "sine", vol = 0.08) => {
    const ctx = ensure();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.value = vol;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  };
  return {
    tick: () => beep(660, 0.05, "square", 0.03),
    correct: () => { beep(880, 0.12, "sine"); setTimeout(() => beep(1320, 0.14, "sine"), 90); },
    wrong: () => beep(180, 0.24, "sawtooth", 0.09),
    combo: () => { beep(880, 0.08); setTimeout(() => beep(1320, 0.08), 60); setTimeout(() => beep(1760, 0.14), 130); },
    steal: () => { beep(220, 0.1, "sawtooth", 0.1); setTimeout(() => beep(660, 0.15, "square"), 100); },
    shield: () => { beep(1200, 0.15, "triangle"); setTimeout(() => beep(800, 0.15, "triangle"), 100); },
    join: () => beep(1200, 0.1, "triangle"),
    reveal: () => { beep(500, 0.12); setTimeout(() => beep(700, 0.12), 100); setTimeout(() => beep(1000, 0.2), 220); },
    podium: () => {
      const notes = [523, 659, 784, 1047];
      notes.forEach((n, i) => setTimeout(() => beep(n, 0.24, "triangle", 0.1), i * 140));
    },
  };
};

// -------- countdown timer hook -------------------------------------
const useCountdown = (startedAt, seconds) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      const left = Math.max(0, seconds - elapsed);
      setRemaining(left);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [startedAt, seconds]);
  return remaining;
};

const OPTION_STYLES = [
  { bg: "bg-red-500 hover:bg-red-600", border: "border-red-700", shape: "▲" },
  { bg: "bg-blue-500 hover:bg-blue-600", border: "border-blue-700", shape: "◆" },
  { bg: "bg-amber-500 hover:bg-amber-600", border: "border-amber-700", shape: "●" },
  { bg: "bg-emerald-500 hover:bg-emerald-600", border: "border-emerald-700", shape: "■" },
];

const REACTION_EMOJIS = ["🔥", "😱", "🎉", "💯", "😂", "🤯", "👏", "💀"];

// -------- Reactions overlay (host projector) — floating emoji stream ------
const ReactionsOverlay = ({ reactions }) => {
  const [live, setLive] = useState([]); // [{key, emoji, x, delay}]
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!reactions || !reactions.length) return;
    const fresh = reactions.filter((r) => !seenRef.current.has(r.id));
    if (!fresh.length) return;
    fresh.forEach((r) => seenRef.current.add(r.id));
    // Add each fresh reaction with a random horizontal position + slight stagger
    const additions = fresh.map((r, i) => ({
      key: r.id,
      emoji: r.emoji,
      x: 6 + Math.random() * 88, // percent
      delay: i * 60,
      variant: Math.random() < 0.5 ? 1 : -1, // sway direction
    }));
    setLive((prev) => [...prev, ...additions]);
    // Auto-purge after animation completes
    const timers = additions.map((a) =>
      setTimeout(() => setLive((prev) => prev.filter((x) => x.key !== a.key)), 3500 + a.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [reactions]);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden z-40">
      <AnimatePresence>
        {live.map((r) => (
          <motion.span
            key={r.key}
            data-testid="floating-reaction"
            initial={{ y: "100%", opacity: 0, scale: 0.4, rotate: r.variant * 12 }}
            animate={{
              y: "-120%",
              opacity: [0, 1, 1, 0.9, 0],
              scale: [0.4, 1.15, 1, 1, 0.9],
              x: [0, r.variant * 30, r.variant * -20, 0],
              rotate: [r.variant * 12, r.variant * -8, r.variant * 12, 0],
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3.2, delay: r.delay / 1000, ease: "easeOut" }}
            style={{
              position: "absolute",
              left: `${r.x}%`,
              bottom: -60,
              fontSize: 60,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.35))",
              lineHeight: 1,
              willChange: "transform, opacity",
            }}
          >
            {r.emoji}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
};

// -------- Reaction bar (student mobile view) ---------------------------
const ReactionBar = ({ gameId, disabled = false }) => {
  const [busy, setBusy] = useState(false);
  const [lastAt, setLastAt] = useState(0);

  const send = async (e) => {
    if (busy) return;
    const now = Date.now();
    if (now - lastAt < 700) return; // client-side floor to avoid 429 spam
    setBusy(true);
    setLastAt(now);
    try {
      await reactInGame(gameId, e);
    } catch (err) {
      // Silent — 429s are fine, spam is expected
    } finally {
      setTimeout(() => setBusy(false), 500);
    }
  };

  return (
    <div className="px-4 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar bg-black/40 border-t border-white/10">
      <span className="text-[10px] uppercase tracking-widest text-white/50 shrink-0 mr-1">React</span>
      {REACTION_EMOJIS.map((e) => (
        <motion.button
          key={e}
          data-testid={`react-${e}`}
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: 1.1 }}
          disabled={disabled}
          onClick={() => send(e)}
          className="text-2xl rounded-full h-11 w-11 shrink-0 grid place-items-center bg-white/5 hover:bg-white/15 active:bg-white/25 border border-white/10 transition disabled:opacity-40"
          aria-label={`React with ${e}`}
        >
          <span style={{ lineHeight: 1 }}>{e}</span>
        </motion.button>
      ))}
    </div>
  );
};

// ============ HOST VIEW (projector) =================================
const HostView = ({ game, onAdvance, onEnd, onPlayAlong, sfx }) => {
  const remaining = useCountdown(game?.question?.started_at, game?.question?.seconds || 20);
  const players = game?.players || [];
  const answered = players.filter((p) => p.has_answered).length;
  const status = game?.status;
  const q = game?.question;

  // Auto-advance when everyone answered OR when timer expires
  const autoAdvanceRef = useRef(false);
  useEffect(() => {
    if (status !== "running" || !q) return;
    if (autoAdvanceRef.current) return;
    if (players.length > 0 && answered >= players.length) {
      autoAdvanceRef.current = true;
      setTimeout(() => { onAdvance(); autoAdvanceRef.current = false; }, 600);
    } else if (remaining <= 0) {
      autoAdvanceRef.current = true;
      onAdvance();
      setTimeout(() => { autoAdvanceRef.current = false; }, 1000);
    }
  }, [status, q?.index, remaining, answered, players.length, onAdvance]);

  // Play tick sound in last 5 seconds
  const lastTickRef = useRef(0);
  useEffect(() => {
    if (status !== "running") return;
    const whole = Math.ceil(remaining);
    if (whole <= 5 && whole !== lastTickRef.current) {
      lastTickRef.current = whole;
      sfx.tick();
    }
  }, [remaining, status, sfx]);

  // Reveal sound
  const lastPhaseRef = useRef("");
  useEffect(() => {
    if (status && status !== lastPhaseRef.current) {
      if (status === "reveal") sfx.reveal();
      if (status === "final") sfx.podium();
      lastPhaseRef.current = status;
    }
  }, [status, sfx]);

  if (status === "waiting") {
    const isRoyale = game.mode === "royale";
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col">
        <ReactionsOverlay reactions={game?.reactions || []} />
        <div className="flex-1 grid place-items-center px-6 py-10">
          <div className="text-center">
            {isRoyale && (
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-red-600/20 border border-red-500/40 px-4 py-1.5 text-xs font-black uppercase tracking-[0.35em] text-red-300">
                <Skull className="h-4 w-4" /> Battle Royale · One wrong = eliminated
              </div>
            )}
            <p className="text-sm sm:text-base uppercase tracking-[0.4em] text-white/60">Lectura Live · Join at</p>
            <p className="mt-2 font-serif text-2xl sm:text-4xl text-white/80">{window.location.host}/join-game</p>
            <div className="mt-8 rounded-3xl border-4 border-amber-400/70 bg-white/5 backdrop-blur-md px-8 sm:px-16 py-6 sm:py-10 shadow-2xl">
              <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-amber-300">Room Code</p>
              <p data-testid="host-room-code" className="font-mono font-black text-6xl sm:text-8xl lg:text-9xl tracking-widest text-white mt-3">
                {game.code}
              </p>
            </div>
            <p className="mt-8 font-serif text-2xl sm:text-3xl">{game.lecture_title}</p>
            <p className="mt-2 text-white/70 text-sm">Hosted by {game.host_name} · {game?.question?.total || players.length ? "" : ""}</p>

            {/* Spectator share link — for parents / co-teachers to follow along read-only */}
            <div className="mt-4">
              <button
                data-testid="host-share-watch"
                onClick={async () => {
                  const link = `${window.location.origin}/game/watch/${game.code}`;
                  await navigator.clipboard.writeText(link);
                  toast.success("Spectator link copied — send to parents or co-teachers");
                }}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-400/15 border border-indigo-300/40 hover:bg-indigo-400/25 px-4 py-2 text-xs font-bold text-indigo-200 transition"
              >
                <Users className="h-3.5 w-3.5" /> Copy spectator link
              </button>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                data-testid="host-start-btn"
                onClick={onAdvance}
                disabled={players.length === 0}
                className="inline-flex items-center gap-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 px-10 py-4 text-lg font-bold shadow-lg transition"
              >
                <Play className="h-5 w-5" /> {players.length === 0 ? "Waiting for players…" : `Start with ${players.length} player${players.length===1?"":"s"}`}
              </button>
              {/* Lets the instructor join their own game as a player — appears with a crown on the leaderboard */}
              {!players.some((p) => p.is_host_player) && (
                <button
                  data-testid="host-play-along-btn"
                  onClick={onPlayAlong}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-300/40 hover:bg-amber-500/25 px-6 py-4 text-sm font-bold text-amber-200 transition"
                >
                  <Crown className="h-4 w-4" /> Play along with my class
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Player joined ribbon */}
        <div className="border-t border-white/10 bg-black/30 backdrop-blur px-6 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-amber-300" />
            <span className="text-xs uppercase tracking-[0.25em] text-white/60">
              {players.length} in the room
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <AnimatePresence>
              {players.map((p) => (
                <motion.span
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.6, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  data-testid={`host-player-${p.id}`}
                  className={`inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 text-sm ${p.is_host_player ? "bg-amber-500/15 border-amber-300/40" : "bg-white/10 border-white/20"}`}
                >
                  <Avatar id={p.avatar} size={22} />
                  {p.name}
                  {p.is_host_player && <Crown className="h-3.5 w-3.5 text-amber-300" />}
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  if (status === "final") {
    const podium = players.slice(0, 3);
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-950 text-white grid place-items-center px-6 py-10 overflow-hidden">
        <ReactionsOverlay reactions={game?.reactions || []} />
        <div className="w-full max-w-5xl">
          <div className="text-center">
            <motion.div initial={{scale: 0.3, opacity:0}} animate={{scale:1, opacity:1}} transition={{type:"spring", stiffness:200}} className="inline-flex items-center gap-3">
              <Crown className="h-10 w-10 text-amber-300" />
              <h1 className="font-serif text-5xl sm:text-7xl font-black">Winners</h1>
              <Crown className="h-10 w-10 text-amber-300" />
            </motion.div>
            <p className="mt-3 text-white/70">{game.lecture_title}</p>
          </div>
          {/* Podium */}
          <div className="mt-10 grid grid-cols-3 gap-4 items-end">
            {[1, 0, 2].map((idx, colI) => {
              const p = podium[idx];
              if (!p) return <div key={colI} />;
              const heights = ["h-40", "h-56", "h-32"]; // 2nd, 1st, 3rd
              const colors = ["from-slate-300 to-slate-500", "from-amber-300 to-amber-500", "from-orange-400 to-orange-700"];
              const icons = [<Medal className="h-8 w-8"/>, <Crown className="h-10 w-10"/>, <Medal className="h-7 w-7"/>];
              return (
                <motion.div
                  key={p.id}
                  data-testid={`podium-${idx + 1}`}
                  initial={{ y: 200, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + colI * 0.35, type: "spring" }}
                  className="text-center"
                >
                  <div className="text-4xl sm:text-6xl font-black inline-flex items-center gap-3">
                    <Avatar id={p.avatar} size={colI === 1 ? 72 : 52} streak={p.best_streak || 0} />
                    <span>{p.name}</span>
                  </div>
                  <p className="mt-2 text-amber-300 font-mono text-2xl sm:text-3xl font-bold">{p.score.toLocaleString()} pts</p>
                  {p.best_streak >= 3 && (
                    <p className="mt-1 text-xs text-white/60 inline-flex items-center gap-1">
                      <Flame className="h-3 w-3" /> best streak {p.best_streak}
                    </p>
                  )}
                  <div className={`mt-4 rounded-t-2xl bg-gradient-to-b ${colors[colI]} ${heights[colI]} flex items-center justify-center text-white shadow-2xl`}>
                    {icons[colI]}
                  </div>
                  <div className="rounded-b-lg bg-black/30 py-1 text-xs uppercase tracking-widest">
                    {["2nd", "1st", "3rd"][colI]}
                  </div>
                </motion.div>
              );
            })}
          </div>
          {/* Rest of leaderboard */}
          {players.length > 3 && (
            <div className="mt-10 rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10 overflow-hidden">
              {players.slice(3, 10).map((p, i) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-white/50 font-mono w-6">#{i + 4}</span>
                    <Avatar id={p.avatar} size={24} />
                    <span className="font-semibold">{p.name}</span>
                    {p.is_host_player && <Crown className="h-3.5 w-3.5 text-amber-300" title="Teacher" />}
                  </div>
                  <span className="text-amber-300 font-mono">{p.score.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-10 text-center">
            <button
              data-testid="host-end-btn"
              onClick={() => { window.location.href = "/"; }}
              className="rounded-full bg-white/10 border border-white/20 hover:bg-white/20 px-8 py-3 text-sm font-semibold transition"
            >
              Back to Lectura
            </button>
          </div>
        </div>
      </div>
    );
  }

  // running or reveal
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col">
      <ReactionsOverlay reactions={game?.reactions || []} />
      <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.3em] text-white/50">Question</span>
          <span data-testid="host-q-index" className="font-mono text-lg font-bold">{(q?.index ?? 0) + 1} / {q?.total ?? 0}</span>
        </div>
        <div className="flex items-center gap-3">
          {status === "running" && (
            <div className={`font-mono text-3xl font-black transition-colors ${remaining <= 5 ? "text-red-400 animate-pulse" : "text-white"}`}>
              {Math.ceil(remaining)}
            </div>
          )}
          <span className="rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs">
            {answered}/{players.length} answered
          </span>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-3 gap-6 p-6 lg:p-10">
        {/* Question + options */}
        <div className="lg:col-span-2 flex flex-col">
          <motion.h2
            key={q?.index}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-serif text-3xl sm:text-5xl font-black leading-tight"
          >
            {q?.question}
          </motion.h2>
          <div className="mt-8 grid grid-cols-2 gap-4 flex-1 content-start">
            {(q?.options || []).map((opt, i) => {
              const isCorrect = status === "reveal" && i === q.correct_index;
              const dim = status === "reveal" && i !== q.correct_index;
              return (
                <div
                  key={i}
                  data-testid={`host-option-${i}`}
                  className={`relative rounded-2xl p-5 sm:p-6 text-lg sm:text-2xl font-bold flex items-center gap-4 shadow-xl transition ${OPTION_STYLES[i].bg} ${dim ? "opacity-30" : ""} ${isCorrect ? "ring-4 ring-white scale-105" : ""}`}
                >
                  <span className="text-3xl sm:text-4xl">{OPTION_STYLES[i].shape}</span>
                  <span className="flex-1">{opt}</span>
                  {isCorrect && <Check className="h-8 w-8" />}
                </div>
              );
            })}
          </div>
          {status === "reveal" && q?.explanation && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl bg-white/10 border border-white/20 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-1">Why</p>
              <p className="text-sm sm:text-base opacity-90 leading-relaxed">{q.explanation}</p>
            </motion.div>
          )}

          {/* Live Reaction Ribbon — animated mascots reacting to the just-revealed answer */}
          {status === "reveal" && players.length > 0 && (
            <div className="mt-6 rounded-2xl bg-black/25 border border-white/10 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50 mb-3">Class reactions</p>
              <div className="flex flex-wrap gap-3">
                {players.slice(0, 30).map((p) => {
                  const reaction = p.last_correct === true ? "correct" : p.last_correct === false ? "wrong" : "idle";
                  const isElim = game?.mode === "royale" && !p.alive;
                  return (
                    <motion.div
                      key={p.id}
                      layout
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex flex-col items-center gap-1 min-w-[54px]"
                    >
                      <Avatar id={p.avatar} size={42} reaction={reaction} streak={p.streak || 0} eliminated={isElim} />
                      <span className="text-[10px] text-white/70 truncate max-w-[68px]">{p.name}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-amber-300" />
            <h3 className="text-sm uppercase tracking-[0.25em] text-white/60">Leaderboard</h3>
          </div>
          <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {players.slice(0, 20).map((p, i) => {
              const isEliminated = game?.mode === "royale" && !p.alive;
              return (
              <li key={p.id} data-testid={`host-lb-${i}`}
                  className={`flex items-center justify-between rounded-lg pl-2 pr-3 py-2 transition ${isEliminated ? "opacity-40 line-through bg-red-900/20" : i === 0 ? "bg-amber-500/25 border border-amber-400/50" : "bg-white/5"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-white/60 font-mono w-5 shrink-0">#{i + 1}</span>
                  <Avatar
                    id={p.avatar}
                    size={26}
                    eliminated={isEliminated}
                    streak={p.streak || 0}
                    reaction={status === "reveal" && !isEliminated ? (p.last_correct === true ? "correct" : p.last_correct === false ? "wrong" : "idle") : "idle"}
                  />
                  <span className="truncate text-sm font-semibold">{p.name}</span>
                  {isEliminated && <Skull className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                  {!isEliminated && p.shields > 0 && <span title="shield" className="text-cyan-300"><Shield className="h-3.5 w-3.5" /></span>}
                  {!isEliminated && p.steals > 0 && <span title="steal ready" className="text-fuchsia-300"><Sword className="h-3.5 w-3.5" /></span>}
                </div>
                <div className="flex items-center gap-2">
                  {status === "reveal" && p.last_delta > 0 && (
                    <span className="text-emerald-300 text-xs font-mono">+{p.last_delta}</span>
                  )}
                  <span className="font-mono font-bold text-amber-300">{p.score}</span>
                </div>
              </li>
              );
            })}
          </ul>
          <button
            data-testid="host-next-btn"
            onClick={onAdvance}
            className="mt-4 w-full rounded-full bg-emerald-500 hover:bg-emerald-600 py-3 font-bold text-sm transition inline-flex items-center justify-center gap-2"
          >
            {status === "reveal" ? <>Next question <ChevronRight className="h-4 w-4" /></> : <>End question early <ChevronRight className="h-4 w-4" /></>}
          </button>
          <button
            data-testid="host-endgame-btn"
            onClick={onEnd}
            className="mt-2 w-full rounded-full bg-white/5 hover:bg-white/10 border border-white/10 py-2 text-xs uppercase tracking-widest text-white/70 transition"
          >
            End game
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ PLAYER VIEW (mobile-first) ============================
const PlayerView = ({ game, onAnswer, sfx }) => {
  const me = (game?.players || []).find((p) => p.is_you);
  const rank = (game?.players || []).findIndex((p) => p.is_you) + 1;
  const remaining = useCountdown(game?.question?.started_at, game?.question?.seconds || 20);
  const status = game?.status;
  const q = game?.question;
  const isRoyale = game?.mode === "royale";
  const isEliminated = isRoyale && me && !me.alive;
  const [chose, setChose] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [powerHold, setPowerHold] = useState(null);
  const lastQ = useRef(-1);

  useEffect(() => {
    if (q && q.index !== lastQ.current) {
      lastQ.current = q.index;
      setChose(null);
      setFeedback(null);
      setPowerHold(null);
    }
  }, [q?.index]);

  const submit = async (idx) => {
    if (chose !== null || !q) return;
    setChose(idx);
    const startMs = new Date(q.started_at).getTime();
    const elapsed = Date.now() - startMs;
    try {
      const res = await onAnswer(idx, elapsed, powerHold);
      setFeedback(res);
      if (res.used_steal) sfx.steal();
      else if (res.used_shield) sfx.shield();
      else if (res.correct && me && me.streak + 1 >= 3) sfx.combo();
      else if (res.correct) sfx.correct();
      else sfx.wrong();
    } catch (e) {
      setChose(null);
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    }
  };

  // Eliminated spectator (royale only) — shown any time our alive=false
  if (isEliminated && status !== "final") {
    const alive = (game.players || []).filter((p) => p.alive).length;
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <div className="px-4 py-3 flex items-center justify-between border-b border-red-800/40 bg-red-950/40">
          <div className="text-xs">
            <span className="uppercase tracking-widest text-red-400 font-black inline-flex items-center gap-1"><Skull className="h-3.5 w-3.5" /> Eliminated</span>
          </div>
          <span className="text-xs text-white/60">{alive} still alive</span>
        </div>
        <div className="flex-1 grid place-items-center px-6">
          <div className="text-center">
            <motion.div initial={{scale:0.3, opacity:0}} animate={{scale:1, opacity:1}} transition={{type:"spring"}} className="mx-auto grid place-items-center h-24 w-24 rounded-full bg-red-500/20 border-2 border-red-500/50">
              <Skull className="h-12 w-12 text-red-400" />
            </motion.div>
            <h2 className="mt-6 font-serif text-4xl font-black text-red-300">You're out</h2>
            <p className="mt-2 text-white/60 text-sm">You survived {me?.eliminated_at_q ?? 0} question{(me?.eliminated_at_q ?? 0) === 1 ? "" : "s"}. Keep watching — someone has to win.</p>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-white/50">Survivors</p>
              <ul className="mt-3 inline-flex flex-col gap-1 text-sm">
                {(game.players || []).filter((p) => p.alive).slice(0, 10).map((p) => (
                  <li key={p.id} className="inline-flex items-center gap-2 rounded-full bg-white/5 px-2 py-1 pl-1">
                    <Avatar id={p.avatar} size={20} streak={p.streak || 0} />
                    {p.name} <span className="text-amber-300 font-mono">{p.score}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "waiting") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-slate-900 text-white flex flex-col">
        <div className="flex-1 grid place-items-center px-6">
        <div className="text-center">
          <Avatar id={me?.avatar} size={80} />
          <p className="mt-5 text-xs uppercase tracking-[0.3em] text-white/60">Waiting for host to start</p>
          <p className="mt-2 font-serif text-3xl">{me?.name}</p>
          <p className="text-white/60 text-sm">You're in the game.</p>
          <p className="mt-6 text-xs text-white/50">Room {game.code}</p>
        </div>
        </div>
        <ReactionBar gameId={game?.id} />
      </div>
    );
  }

  if (status === "final") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-slate-900 text-white grid place-items-center px-6">
        <div className="text-center">
          <Avatar id={me?.avatar} size={110} streak={me?.best_streak || 0} eliminated={isEliminated} />
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-white/60">You finished</p>
          <div className="mt-4 text-8xl font-black text-amber-300 font-mono" data-testid="player-final-score">
            {me?.score ?? 0}
          </div>
          <p className="mt-1 text-lg">points</p>
          <p className="mt-6 font-serif text-3xl">#{rank || "?"} of {game.players.length}</p>
          {me?.best_streak >= 3 && (
            <p className="mt-3 text-orange-300 inline-flex items-center gap-1 text-sm">
              <Flame className="h-4 w-4" /> Best streak: {me.best_streak}
            </p>
          )}
          <button
            data-testid="player-back-btn"
            onClick={() => (window.location.href = "/")}
            className="mt-10 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 px-8 py-3 text-sm font-semibold transition inline-flex items-center gap-2"
          >
            <Home className="h-4 w-4" /> Back to Lectura
          </button>
        </div>
      </div>
    );
  }

  // reveal
  if (status === "reveal") {
    const won = feedback?.correct ?? (me?.last_correct === true);
    const justEliminated = feedback?.eliminated === true;
    return (
      <div className={`min-h-screen text-white flex flex-col ${justEliminated ? "bg-red-900" : won ? "bg-emerald-600" : "bg-rose-700"} transition-colors`}>
        <div className="flex-1 grid place-items-center px-6"><div className="text-center">
          <Avatar
            id={me?.avatar}
            size={100}
            reaction={justEliminated ? "wrong" : won ? "correct" : "wrong"}
            streak={me?.streak || 0}
            eliminated={justEliminated}
          />
          <motion.p initial={{scale: 0.3, opacity:0}} animate={{scale:1, opacity:1}} className="mt-4 font-serif text-6xl font-black" data-testid="player-reveal-title">
            {justEliminated ? "ELIMINATED" : feedback?.used_shield ? "Shield saved you!" : won ? "Correct!" : "Missed it"}
          </motion.p>
          {justEliminated && <p className="mt-3 text-red-200 text-sm inline-flex items-center gap-1"><Skull className="h-4 w-4" /> You're out of the Battle Royale.</p>}
          {won && (feedback?.delta || me?.last_delta) > 0 && (
            <p className="mt-4 text-3xl font-mono text-amber-200">+{feedback?.delta || me?.last_delta}</p>
          )}
          <p className="mt-8 text-xs uppercase tracking-[0.3em] opacity-70">Your score</p>
          <p className="text-5xl font-black font-mono">{me?.score ?? 0}</p>
          <p className="mt-1 text-sm opacity-90">#{rank} of {game.players.length}</p>
          {me?.streak >= 3 && !justEliminated && (
            <p className="mt-4 inline-flex items-center gap-1 text-orange-200"><Flame className="h-4 w-4"/> {me.streak} in a row!</p>
          )}
          {feedback?.used_steal && (
            <p className="mt-3 text-fuchsia-200 inline-flex items-center gap-1"><Sword className="h-4 w-4"/> Stole 200 pts from the leader!</p>
          )}
        </div></div>
        <ReactionBar gameId={game?.id} />
      </div>
    );
  }

  // running
  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="text-xs flex items-center gap-2">
          <Avatar id={me?.avatar} size={26} streak={me?.streak || 0} />
          <div>
            <span className="uppercase tracking-widest text-white/60 block leading-tight">{me?.name}</span>
            <span className="font-mono text-amber-300 font-bold">{me?.score ?? 0}</span>
          </div>
        </div>
        <div className={`font-mono text-2xl font-black ${remaining <= 5 ? "text-red-400 animate-pulse" : "text-white/90"}`}>
          {Math.ceil(remaining)}
        </div>
      </div>
      {/* Question preview at top on mobile */}
      <div className="px-6 pt-4 pb-3 border-b border-white/5">
        <p className="text-xs uppercase tracking-widest text-white/50">Q {q?.index + 1} / {q?.total}</p>
        <p className="mt-1 font-serif text-lg sm:text-2xl leading-snug line-clamp-3">{q?.question}</p>
      </div>

      {/* Power-ups */}
      {(me?.shields > 0 || me?.steals > 0) && chose === null && (
        <div className="px-6 py-3 flex items-center gap-2 bg-black/30">
          {me.shields > 0 && (
            <button
              data-testid="player-power-shield"
              onClick={() => setPowerHold(powerHold === "shield" ? null : "shield")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border transition ${powerHold === "shield" ? "bg-cyan-400 text-slate-900 border-cyan-200 scale-105" : "bg-cyan-500/20 text-cyan-100 border-cyan-400/40"}`}
            >
              <Shield className="h-3.5 w-3.5" /> Shield ×{me.shields}
            </button>
          )}
          {me.steals > 0 && (
            <button
              data-testid="player-power-steal"
              onClick={() => setPowerHold(powerHold === "steal" ? null : "steal")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border transition ${powerHold === "steal" ? "bg-fuchsia-400 text-slate-900 border-fuchsia-200 scale-105" : "bg-fuchsia-500/20 text-fuchsia-100 border-fuchsia-400/40"}`}
            >
              <Sword className="h-3.5 w-3.5" /> Steal ×{me.steals}
            </button>
          )}
          {powerHold && (
            <span className="text-xs text-white/70">
              {powerHold === "shield" ? "Shield armed — a wrong answer will preserve your streak" : "Steal armed — a correct answer will steal 200 pts from the leader"}
            </span>
          )}
        </div>
      )}

      {me?.combo_boost_pending && chose === null && (
        <div className="px-6 py-2 flex items-center gap-2 bg-amber-500/15 border-y border-amber-400/30">
          <Zap className="h-4 w-4 text-amber-300" />
          <span className="text-xs font-bold text-amber-100 uppercase tracking-widest">Combo Boost active — 2× points on your next correct answer!</span>
        </div>
      )}

      {/* Big 4 buttons */}
      <div className="flex-1 grid grid-cols-2 gap-3 p-4">
        {(q?.options || []).map((opt, i) => {
          const isChosen = chose === i;
          return (
            <button
              key={i}
              data-testid={`player-option-${i}`}
              onClick={() => submit(i)}
              disabled={chose !== null}
              className={`relative rounded-2xl p-5 sm:p-8 text-lg sm:text-2xl font-black text-white shadow-xl transition-all ${OPTION_STYLES[i].bg} ${chose !== null && !isChosen ? "opacity-30" : ""} ${isChosen ? "ring-4 ring-white scale-95" : "active:scale-95"}`}
            >
              <span className="absolute top-3 left-3 text-3xl opacity-80">{OPTION_STYLES[i].shape}</span>
              <span className="block break-words pl-8">{opt}</span>
            </button>
          );
        })}
      </div>

      {chose !== null && (
        <div className="px-6 pb-3 text-center text-white/70 text-sm">
          Answer locked in. Wait for the reveal…
        </div>
      )}

      <ReactionBar gameId={game?.id} />
    </div>
  );
};

// ============ MAIN LiveGame page ====================================
export const LiveGame = ({ mode = "auto" }) => {
  // mode 'host' if we started the game; 'player' if we joined
  const { user } = useAuth();
  const sfx = useSfx();
  const params = useParams();
  const location = useLocation();
  const [game, setGame] = useState(null);
  const [role, setRole] = useState(null); // 'host' | 'player'
  const [joinCode, setJoinCode] = useState("");
  const [joinName, setJoinName] = useState("");
  const [loading, setLoading] = useState(false);
  const [gameId, setGameId] = useState(null);

  // Read from URL on mount: /game/host/:id (host) or /join-game?code=XXX (player)
  useEffect(() => {
    if (mode === "host" && params.id) {
      setRole("host");
      setGameId(params.id);
      return;
    }
    const search = new URLSearchParams(location.search || "");
    const codeParam = search.get("code");
    if (codeParam) setJoinCode(codeParam.toUpperCase());
  }, [mode, params.id, location.search]);

  // Auto-join if we arrived with ?code=XXX (e.g., from a pop-quiz push)
  const autoJoinedRef = useRef(false);
  useEffect(() => {
    if (autoJoinedRef.current) return;
    if (role || game) return;
    if (!joinCode || joinCode.length < 4) return;
    const search = new URLSearchParams(location.search || "");
    if (search.get("code")) {
      autoJoinedRef.current = true;
      doJoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCode]);

  // Poll for state
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const g = await getGame(gameId);
        if (!cancelled) setGame(g);
      } catch (e) {
        // if 404, game ended — bail
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [gameId]);

  const doJoin = async () => {
    if (!joinCode.trim()) return toast.error("Enter a room code");
    setLoading(true);
    try {
      const g = await joinGame(joinCode.trim().toUpperCase(), joinName.trim());
      setGame(g);
      setGameId(g.id);
      setRole("player");
      sfx.join();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join");
    } finally {
      setLoading(false);
    }
  };

  const doAdvance = async () => {
    if (!gameId) return;
    try { const g = await advanceGame(gameId); setGame(g); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't advance"); }
  };

  const doEnd = async () => {
    if (!gameId) return;
    if (!window.confirm("End the game and show the podium?")) return;
    try { const g = await endGame(gameId); setGame(g); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't end"); }
  };

  const doAnswer = async (choice, elapsed, power) => {
    return await answerGame(gameId, choice, elapsed, power);
  };

  const doPlayAlong = async () => {
    if (!game?.code) return;
    try {
      // Instructor joins their own game as a player — appears on leaderboard with crown.
      // Uses their account name/avatar, so students see "Mr. Doe" is playing with them.
      const g = await joinGame(game.code, user?.name || "");
      setGame(g);
      sfx.join();
      toast.success("You're in the game! Students will see you with a 👑");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't play along");
    }
  };

  const copyCode = async () => {
    if (!game?.code) return;
    await navigator.clipboard.writeText(game.code);
    toast.success("Room code copied");
  };

  // Not in a game yet — show lobby
  if (!role || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white grid place-items-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-400/20 border border-amber-400/40 px-4 py-1 text-xs uppercase tracking-[0.35em] text-amber-200">
              <Sparkles className="h-3.5 w-3.5" /> Lectura Live
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-black">Join a live game</h1>
            <p className="mt-2 text-white/70 text-sm">Enter the room code your teacher shared.</p>
          </div>
          <div className="mt-8 space-y-4">
            <input
              data-testid="join-code-input"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ROOM CODE"
              className="w-full rounded-2xl bg-white/10 border-2 border-white/20 focus:border-amber-400 outline-none px-6 py-5 text-center font-mono text-3xl font-black tracking-widest text-white uppercase placeholder:text-white/30 transition"
            />
            <input
              data-testid="join-name-input"
              value={joinName}
              onChange={(e) => setJoinName(e.target.value.slice(0, 24))}
              placeholder={`Your name (${user?.name || (user?.email || "Player").split("@")[0]})`}
              className="w-full rounded-full bg-white/5 border border-white/20 focus:border-amber-400 outline-none px-5 py-3 text-white placeholder:text-white/40 transition"
            />
            <button
              data-testid="join-game-btn"
              onClick={doJoin}
              disabled={loading || !joinCode}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 py-4 font-bold text-lg shadow-lg transition"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Join game <ChevronRight className="h-5 w-5" /></>}
            </button>
          </div>
          <div className="mt-8 text-center">
            <button onClick={() => (window.location.href = "/")} className="text-xs text-white/50 hover:text-white/80 transition">
              Back to Lectura
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (role === "host") {
    // Jeopardy renders its own board/judge UI; LiveGame handles classic & royale only
    if (game?.kind === "jeopardy") {
      return <JeopardyGame mode="host" initialGame={game} />;
    }
    return (
      <>
        <HostView game={game} onAdvance={doAdvance} onEnd={doEnd} onPlayAlong={doPlayAlong} sfx={sfx} />
      </>
    );
  }
  // Player path — route Jeopardy to its own component (uses the game code to keep polling)
  if (game?.kind === "jeopardy") {
    return <JeopardyGame mode="player" initialGame={game} codeOverride={game.code} />;
  }
  return <PlayerView game={game} onAnswer={doAnswer} sfx={sfx} />;
};

export default LiveGame;
